import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useActions, useAnalytics, useBreakpoint } from '@app/hooks';
import SummaryAndPaymentPage from '@app/components/pages/summary-and-payment/Container';
import Conditional from '@app/components/common/Conditional/Conditional';
import * as checkoutActionsList from '@app/store/checkout/actions';
import { getGACheckoutProducts } from '@app/hooks/useAnalytics/utils';
import { LIST_CHECKOUT_SUMMARY_AND_PAYMENT } from '@app/constants/analyticsConstants';
import {
  BILLING_METHOD_CAT_PREPAID_CERT,
  DEFAULT_OPTION,
  PAGE_BREAKPOINTS
} from '@app/constants/commonConstants';
import { PCC_PS_HIDE_ESPOTS_MOBILE } from '@app/constants/featureFlags';
import {
  getCatVantageRewardsBalance,
  getIsEnrolledUser
} from '@app/store/catvantage/actions';
import { useTranslation } from 'react-i18next';
import ShoppingPreferencesEspot from './ShoppingPreferencesEspot';
import { convertFormattedNumberToUnformatted } from '../utils';

const CheckoutSummaryAndPayment = ({ promoCode }) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { loadSummaryPage } = useActions(checkoutActionsList);
  const currentOrderId = useSelector(s => s?.orders?.current);
  const currentOrder = useSelector(s => s?.orders?.byId?.[currentOrderId]);
  const currencyNotation = useSelector(s => s?.orders?.currencyNotation);
  const includeGST = useSelector(s => s?.orders?.includeGST);
  const savedCreditCard = useSelector(s => s?.orders?.savedCreditCard);
  const requestByDate = useSelector(s => s?.orders?.requestByDate?.value);
  const micoEnabled = useSelector(s => s?.orders?.micoEnabled);
  const current = useSelector(s => s?.orders?.current);
  const punchoutOrderFailed = useSelector(s => s?.orders?.punchoutOrderFailed);
  const quoteEnabled = useSelector(s => s?.orders?.quoteEnabled);
  const showGST = useSelector(s => s?.orders?.showGST);
  const vatTaxEnabled = useSelector(s => s?.orders?.vatTaxEnabled);
  const zeroFreightMessageEnabled = useSelector(
    s => s?.orders?.zeroFreightMessageEnabled
  );

  const skipCreditCard = useSelector(s => s?.checkout?.skipCreditCard);
  const isSAndPLoading = useSelector(s => s?.checkout?.isSAndPLoading);
  const paymentGateway = useSelector(s => s?.checkout?.paymentGateway);
  const orderTotals = useSelector(s => s?.checkout?.orderTotals);
  const catCreditTotal = useSelector(s => s?.checkout?.catCreditTotals);
  const addresses = useSelector(s => s?.checkout?.addresses);
  const paymentTokenizationEnabled = useSelector(
    s => s?.checkout?.paymentTokenizationEnabled
  );
  const selectedBillingAddress = useSelector(
    s => s?.checkout?.selectedBillingAddress
  );
  const isSAndPLoadSuccessful = useSelector(
    s => s?.checkout?.isSAndPLoadSuccessful
  );
  const selectedBillingMethod = useSelector(
    s => s?.checkout?.selectedBillingMethod
  );
  const selectedAlternativePayment = useSelector(
    s => s?.checkout?.selectedAlternativePayment
  );
  const selectedAdditionalBillingMethod = useSelector(
    s => s?.checkout?.selectedAdditionalBillingMethod
  );
  const billingMethodOptions = useSelector(
    s => s?.checkout?.billingMethodOptions
  );

  const hasTrackedEvent = useRef(false);
  const { fireProductImpressionEvent } = useAnalytics();

  const orderItems = useSelector(
    s => s.orders?.byId?.[currentOrderId]?.ascendingOrderItems
  );
  const fulfillmentType = useSelector(
    s => s.orders?.byId?.[currentOrderId]?.fullfillment?.type
  );
  const billingMethod = useSelector(
    s => s?.checkout?.selectedBillingMethod?.displayName
  );
  const isCatVantageRewardsEnabled = useSelector(
    state => state.featureFlag.PCC_CatVantageRewardsFlag
  );
  const isEnrolledInCatVantage = useSelector(
    s => s.catVantage.isCatVantageEnrolled
  );
  const isCatCreditInternationalPhaseOne = useSelector(
    state => state.dealer?.isCreditsInternationalPhaseOneEnabled
  );
  const isCatCreditInternationalPhaseTwo = useSelector(
    state => state.dealer?.isCreditsInternationalPhaseTwoEnabled
  );

  const psESpotHidingMobileViewFlag = useSelector(
    state => state.featureFlag[PCC_PS_HIDE_ESPOTS_MOBILE]
  );

  const isCatCreditInternational =
    isCatCreditInternationalPhaseOne || isCatCreditInternationalPhaseTwo;

  const checkoutOrderTotals =
    orderTotals && currentOrderId === orderTotals?.orderId
      ? orderTotals
      : currentOrder?.orderTotals;

  const isCatCreditBillingMethod =
    selectedBillingMethod?.value === BILLING_METHOD_CAT_PREPAID_CERT;

  const catCreditTotals =
    catCreditTotal && currentOrderId === catCreditTotal?.orderId
      ? catCreditTotal
      : currentOrder?.orderTotals;

  const checkoutCatCreditOrderTotals =
    !isCatCreditInternational && isCatCreditBillingMethod
      ? catCreditTotals
      : {
          ...checkoutOrderTotals,
          unformattedOrderTotal:
            checkoutOrderTotals?.unformattedOrderTotal ||
            convertFormattedNumberToUnformatted(
              checkoutOrderTotals?.orderTotal
            ),
          unformattedOrderAdjustment:
            checkoutOrderTotals?.unformattedOrderAdjustment ||
            convertFormattedNumberToUnformatted(
              checkoutOrderTotals?.orderAdjustment
            )
        };

  useEffect(() => {
    loadSummaryPage({
      ...DEFAULT_OPTION,
      displayName: t('BILL_BILLING_SELECT_BILLING_METHOD')
    });
  }, [loadSummaryPage]);

  const userType = useSelector(s => s?.common?.userType);
  const isCSPCustomer = useSelector(s => s?.common?.isCSPCustomer);

  useEffect(() => {
    if (
      isEmpty(orderItems) ||
      hasTrackedEvent.current ||
      isEmpty(billingMethod)
    ) {
      return;
    }
    let checkoutType = '';
    if (userType === 'R' || userType === 'A') {
      if (isCSPCustomer) {
        checkoutType = 'Logged In B2B';
      } else {
        checkoutType = 'Logged In IA';
      }
    } else {
      checkoutType = 'Guest';
    }
    const gaProducts = getGACheckoutProducts({
      products: orderItems
    });
    fireProductImpressionEvent(LIST_CHECKOUT_SUMMARY_AND_PAYMENT, gaProducts);
    hasTrackedEvent.current = true;
  }, [
    billingMethod,
    orderItems,
    fireProductImpressionEvent,
    isCSPCustomer,
    userType
  ]);

  useEffect(() => {
    if (isCatVantageRewardsEnabled && isCSPCustomer) {
      dispatch(getIsEnrolledUser());
      if (isEnrolledInCatVantage) {
        dispatch(getCatVantageRewardsBalance());
      }
    }
  }, [
    dispatch,
    isCatVantageRewardsEnabled,
    isEnrolledInCatVantage,
    isCSPCustomer
  ]);

  return (
    <>
      <SummaryAndPaymentPage
        billingAddress={addresses}
        billingMethodOptions={billingMethodOptions}
        currencyNotation={currencyNotation}
        includeGST={includeGST}
        isSAndPLoading={isSAndPLoading}
        isSAndPLoadSuccessful={isSAndPLoadSuccessful}
        micoEnabled={micoEnabled}
        orderId={current}
        orderTotals={checkoutCatCreditOrderTotals}
        paymentGateway={paymentGateway}
        paymentTokenizationEnabled={paymentTokenizationEnabled}
        promoCode={promoCode}
        punchoutOrderFailed={punchoutOrderFailed}
        quoteEnabled={quoteEnabled}
        requestByDate={requestByDate}
        savedCreditCard={savedCreditCard}
        selectedBillingMethod={selectedBillingMethod}
        selectedAlternativePayment={selectedAlternativePayment}
        selectedAdditionalBillingMethod={selectedAdditionalBillingMethod}
        selectedBillingAddress={selectedBillingAddress}
        showGST={showGST}
        skipCreditCard={skipCreditCard}
        vatTaxEnabled={vatTaxEnabled}
        zeroFreightMessageEnabled={zeroFreightMessageEnabled}
      />
      <Conditional test={isMD || !psESpotHidingMobileViewFlag}>
        <ShoppingPreferencesEspot />
      </Conditional>
    </>
  );
};

export default CheckoutSummaryAndPayment;
CheckoutSummaryAndPayment.propTypes = {
  promoCode: PropTypes.string
};
